import { assign, filter, find, forEach, map, sortBy } from 'lodash';
import React from 'react';
import * as actions from '../actions';
import componentServices from '../componentServices';
import { AsyncButton } from './sharedComponents';
import { changeAccountingSummaryShaper } from '../reportActions';
import { getModel, ModelCollection } from 'Scripts/store';
import globalRelay from 'Scripts/globalRelay';
import { getInstallSheets } from 'Scripts/schemaHelper';

const _reports = [{
	name: 'Glass',
	key: 'internal/glass',
	filterTest: ({ jobCost }) => !!jobCost.getLineItemsByType(['glass']).length
}, {
	name: 'Outside Supplier',
	key: 'internal/outside-supplier',
	filterTest: ({ jobCost }) => !!jobCost.getLineItemsByType(['outside-supplier', 'special-order-glass']).length
}, {
	name: 'Outside Supplier (without pricing)',
	key: 'internal/outside-supplier-noprice',
	filterTest: ({ jobCost }) => !!jobCost.getLineItemsByType(['outside-supplier', 'special-order-glass']).length
}, {
	name: 'Warehouse',
	key: 'internal/warehouse',
	filterTest: ({ jobCost }) => !!jobCost.getLineItemsByType('warehouse').length
}, {
	name: 'Warehouse (without price)',
	key: 'internal/warehouse-noprice',
	filterTest: ({ jobCost }) => !!jobCost.getLineItemsByType('warehouse').length
}, {
	name: 'Misc',
	key: 'internal/misc',
	filterTest: ({ jobCost }) => !!jobCost.getLineItemsByType('misc').length
}, {
	expand: ({ jobCost, reportList }) => {
		const installSheets = getInstallSheets(jobCost);
		const sheetCount = installSheets.length();

		forEach(sortBy(installSheets, ['sortIndex']), sheet => {
			const lineItems = filter(jobCost.getLineItemsByType(['installation', 'installation-custom', 'service']), { installSheetKey: sheet.key });
			if (lineItems.length) {
				reportList.push({ key: `internal/installation/${sheet.key}`, name: sheetCount > 1 ? `Installation ${sheet.sortIndex + 1}` : 'Installation' });
			}
		});
	}
}, {
	name: 'Sub Trades',
	key: 'internal/subtrade-jobcost',
	filterTest: ({ jobCost }) => !!jobCost.getLineItemsByType('subtrade').length
}, {
	name: 'Summary',
	key: 'internal/jobcostsummary'
}, {
	name: 'Commission',
	key: 'internal/jobcostcommission',
	filterTest: ({ currentEmployee, job, jobCost }) => job && currentEmployee.key === job.accountManagerKey && (jobCost.salesCommissionPercent || currentEmployee.salesCommissionPercent)
}, {
	name: 'Referral Fee',
	key: 'internal/referral-fee',
	filterTest: ({ jobCost }) => !!jobCost.getLineItemsByType('referral-fee').length
}, { 
	name: 'Cabinet Specifications Summary',
	key: 'internal/cabinetspecssummary',  
	filterTest: ({ jobCost }) => !jobCost.currentChangeOrder && globalRelay.config!.companyKey === "legacy-kitchens"
},{
	name: 'Accounting Summary',
	key: 'changeorder/accounting',
	filterTest: ({ jobCost }) => jobCost.currentChangeOrder && jobCost.currentChangeOrder.type === 'change-order' && jobCost.currentChangeOrder.accountingId,
	shaper: changeAccountingSummaryShaper
}];

export default class JobCostEditReports extends React.Component<any, any> {
	constructor(props) {
		super(props);

		this.state = this.getStateFromProps();

		this.handleAllReportsChanged = this.handleAllReportsChanged.bind(this);
		this.printSelected = this.printSelected.bind(this);
	}

	UNSAFE_componentWillReceiveProps() {
		this.setState(this.getStateFromProps());
	}

	componentWillUnmount() {
		actions.setPrintChildren(null);
	}

	getStateFromProps() {
		const { jobCost } = this.props;
		const { store } = componentServices;
		const job = getModel({ store, collection: ModelCollection.jobs, key: jobCost.jobKey });
		const builder = job && job.builderKey && getModel({ store, collection: ModelCollection.contractors, key: job.builderKey});
		const state = assign({ allSelected: false }, this.state);

		if (!state.reports) {
			const currentEmployee = componentServices.securityContext.getCurrentEmployee();
			const filterParams = { builder, currentEmployee, job, jobCost };
			state.reports = [];

			forEach(filter(_reports, section => !section.filterTest || section.filterTest(filterParams)), r => {
				if (r.expand) {
					r.expand({ jobCost, reportList: state.reports });
				}
				else {
					state.reports.push(assign({ selected: false }, r));
				}
			});
		}

		return state;
	}

	handleAllReportsChanged(e) {
		const selected = e.target.checked;

		this.setState(previousState => {
			const state = assign({}, previousState);

			forEach(filter(state.reports, report => !report.component), report => {
				report.selected = selected;
			});

			return state;
		});
	}

	handleReportChange(key, e) {
		const selected = e.target.checked;

		this.setState(previousState => {
			const state = assign({}, previousState);

			const report = find(state.reports, { key: key });
			if (report) {
				report.selected = selected;
			}

			return state;
		});
	}

	printSelected() {
		const { fetchReport } = this.props;
		const { reports } = this.state;
		const shapers: any[] = [];
		const selectedKeys = map(filter(reports, report => report.selected), report => {
			if (report.shaper) {
				shapers.push(report.shaper);
			}
			return report.key.split('/').slice(1).join('/');
		});

		const shaper = (data, ...args) => {
			forEach(shapers, s => {
				data = s(data, ...args);
			});

			return data;
		};

		return fetchReport(`internal/aggregate?sections=${selectedKeys.join(',')}`, shaper);
	}

	printReport(key) {
		const { fetchReport } = this.props;
		const { reports } = this.state;
		const report = find(reports, { key });

		if (report.component) {
			const { jobCost } = this.props;
			const { store } = componentServices;
			const job =  getModel({ store, collection: ModelCollection.jobs, key: jobCost.jobKey });
			const builder = job && job.builderKey && getModel({ store, collection: ModelCollection.contractors, key: job.builderKey });
			const Child = report.component;
			const doPrint = () => {
				window.print();
				setTimeout(() => {
					actions.setPrintChildren(null);
				}, 200);
			};

			actions.setPrintChildren(() => <Child builder={builder} job={job} jobCost={jobCost} onReady={doPrint} />);
		}
		else {
			return fetchReport(key, report.shaper);
		}
	}

	render() {
		const { reports } = this.state;
		let allSelected = true;
		let someSelected = false;

		const rows = map(reports, report => {
			if (report.selected) {
				someSelected = true;
			}
			else if (!report.component) {
				allSelected = false;
			}

			return (<tr key={report.key}>
				<td />
				<td><label>{report.component ? null : <input type="checkbox" checked={report.selected} onChange={this.handleReportChange.bind(this, report.key)} />}{report.name}</label></td>
				<td className="action"><button className="icon iconOnly pdf" onClick={this.printReport.bind(this, report.key)}><span /></button></td>
				<td />
			</tr>);
		});

		return (
			<section>
				<header>
					<span>Reports</span>
					<div className="actions">
						<AsyncButton className={!someSelected ? 'disabled' : ''} asyncAction={this.printSelected} label="Print Selected" />
					</div>
				</header>
				<div className="inner">
					<table className="compact" cellPadding="0" cellSpacing="0">
						<thead>
							<tr>
								<td />
								<td><label><input type="checkbox" checked={allSelected} onChange={this.handleAllReportsChanged} />Report Name</label></td>
								<td className="action">View</td>
								<td />
							</tr>
						</thead>
						<tbody>
							{rows}
						</tbody>
					</table>
				</div>

			</section>
		);
	}
}
